import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";  
import { useNavigate, useLocation } from "react-router-dom";  
import Navbar from "./Navbar";  
import api from "../api";  
import { motion, AnimatePresence } from "framer-motion";  
  
function TradeLogs() {  
  const navigate = useNavigate();  
  const location = useLocation();  
  
  const [liveTrades, setLiveTrades] = useState([]);  
  const [closedTrades, setClosedTrades] = useState([]);  
  const [summary, setSummary] = useState(null);  
  const [activeTab, setActiveTab] = useState("closed");  
  const [expandedTrade, setExpandedTrade] = useState(null);  
  const [highlightedId, setHighlightedId] = useState(null);  
  const [initialLoading, setInitialLoading] = useState(true);  
  const [notification, setNotification] = useState(null);  
  const [liveTradesError, setLiveTradesError] = useState(false);  
  
  const [showFilters, setShowFilters] = useState(false);  
  const [searchTerm, setSearchTerm] = useState("");  
  const [filterAsset, setFilterAsset] = useState("all");  
  const [filterSide, setFilterSide] = useState("all");  
  const [filterResult, setFilterResult] = useState("all");  
  const [sortBy, setSortBy] = useState("timestamp");  
  const [sortOrder, setSortOrder] = useState("desc");  
  
  const showNotification = useCallback((message, type = "info") => {  
    setNotification({ message, type });  
    setTimeout(() => setNotification(null), 4000);  
  }, []);  
  
  /* ─── Auth guard ─── */  
  useEffect(() => {  
    if (!localStorage.getItem("access_token")) navigate("/login");  
  }, [navigate]);  
  
  /* ─── Handle highlight from Dashboard nav ─── */  
  useEffect(() => {  
    const id = location.state?.highlightTrade;  
    if (!id) return;  
    setActiveTab("closed");  
    setExpandedTrade(id);  
    setHighlightedId(id);  
    const t1 = setTimeout(() => {  
      document.getElementById(`trade-${id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });  
    }, 350);  
    const t2 = setTimeout(() => setHighlightedId(null), 3500);  
    return () => {  
      clearTimeout(t1);  
      clearTimeout(t2);  
    };  
  }, [location.state]);  
  
  /* ─── Fetchers ─── */  
  const fetchTradeLogs = useCallback(async () => {  
    try {  
      const params = { limit: 10000 };  
      if (filterAsset !== "all") params.asset_type = filterAsset;  
      const res = await api.get("/logs/trade_logs", { params });  
      setClosedTrades(res.data.closed_trades || res.data.closed || []);  
      setSummary(res.data.summary);  
    } catch (error) {  
      console.error("[Trade Logs Error]", error);  
      if (error.response?.status === 401) {  
        showNotification("Session expired. Please sign in again.", "error");  
        setTimeout(() => navigate("/login"), 1500);  
      } else {  
        showNotification("Failed to load trades", "error");  
      }  
    } finally {  
      setInitialLoading(false);  
    }  
  }, [navigate, filterAsset, showNotification]);  
  
  const fetchLiveTrades = useCallback(async () => {  
    try {  
      const params = {};  
      if (filterAsset !== "all") params.asset_type = filterAsset;  
      const res = await api.get("/logs/live_trades", { params });  
      const positions = res.data.positions || res.data || [];  
      setLiveTrades(Array.isArray(positions) ? positions : []);  
      setLiveTradesError(false);  
    } catch (error) {  
      console.error("[Live Trades Error]", error);  
      setLiveTradesError(true);  
      setLiveTrades([]);  
    }  
  }, [filterAsset]);  
  
  useEffect(() => {  
    fetchTradeLogs();  
    fetchLiveTrades();  
    const interval = setInterval(() => {  
      if (activeTab === "live") fetchLiveTrades();  
    }, 15000);  
    return () => clearInterval(interval);  
  }, [fetchTradeLogs, fetchLiveTrades, activeTab]);  
  
  /* ─── Derived data ─── */  
  const activeFilterCount = useMemo(() => {  
    let n = 0;  
    if (filterAsset !== "all") n++;  
    if (filterSide !== "all") n++;  
    if (filterResult !== "all" && activeTab === "closed") n++;  
    if (searchTerm.trim()) n++;  
    return n;  
  }, [filterAsset, filterSide, filterResult, searchTerm, activeTab]);  
  
  const clearFilters = () => {  
    setSearchTerm("");  
    setFilterAsset("all");  
    setFilterSide("all");  
    setFilterResult("all");  
  };  
  
  const filteredTrades = useMemo(() => {  
    let trades = activeTab === "live" ? [...liveTrades] : [...closedTrades];  
  
    if (searchTerm) {  
      const q = searchTerm.toLowerCase();  
      trades = trades.filter(  
        (t) =>  
          t.symbol?.toLowerCase().includes(q) ||  
          t.side?.toLowerCase().includes(q) ||  
          t.asset_type?.toLowerCase().includes(q)  
      );  
    }  
    if (filterAsset !== "all") {  
      trades = trades.filter((t) => t.asset_type?.toUpperCase() === filterAsset.toUpperCase());  
    }  
    if (filterSide !== "all") {  
      trades = trades.filter((t) => t.side?.toLowerCase() === filterSide.toLowerCase());  
    }  
    if (activeTab === "closed" && filterResult !== "all") {  
      trades = trades.filter((t) =>  
        filterResult === "profit" ? (t.pnl || 0) > 0 : (t.pnl || 0) <= 0  
      );  
    }  
  
    trades.sort((a, b) => {  
      let aVal, bVal;  
      switch (sortBy) {  
        case "pnl": aVal = a.pnl || 0; bVal = b.pnl || 0; break;  
        case "symbol": aVal = a.symbol || ""; bVal = b.symbol || ""; break;  
        case "asset": aVal = a.asset_type || ""; bVal = b.asset_type || ""; break;  
        default: aVal = new Date(a.timestamp); bVal = new Date(b.timestamp);  
      }  
      return sortOrder === "asc" ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);  
    });  
  
    return trades;  
  }, [activeTab, liveTrades, closedTrades, searchTerm, filterAsset, filterSide, filterResult, sortBy, sortOrder]);  
  
  const displaySummary = useMemo(() => {  
    if (activeTab === "live" || filteredTrades.length === 0) return summary;  
    const totalPnl = filteredTrades.reduce((s, t) => s + (t.pnl || 0), 0);  
    const wins = filteredTrades.filter((t) => (t.pnl || 0) > 0).length;  
    return {  
      ...summary,  
      total_trades: filteredTrades.length,  
      total_pnl: totalPnl,  
      win_rate: filteredTrades.length ? +((wins / filteredTrades.length) * 100).toFixed(2) : 0,  
      win_count: wins,  
      loss_count: filteredTrades.length - wins,  
    };  
  }, [activeTab, filteredTrades, summary]);  
  
  const downloadCSV = () => {  
    const headers = ["Asset", "Symbol", "Side", "Entry", "Exit", "P&L", "ATR", "Exit Reason", "Timestamp"];  
    const rows = filteredTrades.map((t) => [  
      t.asset_type || "N/A",  
      t.symbol,  
      t.side,  
      t.entry,  
      t.exit_price || t.exit,  
      t.pnl,  
      t.atr_value || "N/A",  
      t.exit_reason || "N/A",  
      new Date(t.timestamp).toLocaleString(),  
    ]);  
    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");  
    const blob = new Blob([csv], { type: "text/csv" });  
    const link = document.createElement("a");  
    link.href = URL.createObjectURL(blob);  
    link.download = `trade_logs_${new Date().toISOString().split("T")[0]}.csv`;  
    link.click();  
    showNotification("CSV downloaded", "success");  
  };  
  
  const toggleTradeExpansion = (id) => setExpandedTrade(expandedTrade === id ? null : id);  
  
  /* ─── Loading ─── */  
  if (initialLoading) {  
    return (  
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">  
        <Navbar />  
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">  
          <div className="text-center">  
            <div className="relative w-14 h-14 mx-auto mb-4">  
              <div className="absolute inset-0 border-4 border-gold/20 rounded-full" />  
              <div className="absolute inset-0 border-4 border-gold border-t-transparent rounded-full animate-spin" />  
            </div>  
            <p className="text-gold text-sm font-semibold">Loading your trades…</p>  
          </div>  
        </div>  
      </div>  
    );  
  }  
  
  return (  
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black text-white">  
      <Navbar />  
  
      {/* Notification */}  
      <AnimatePresence>  
        {notification && (  
          <motion.div  
            initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}  
            className={`fixed top-20 left-4 right-4 sm:left-auto sm:right-6 z-50 px-5 py-3.5 rounded-xl shadow-2xl border backdrop-blur-md sm:max-w-md ${  
              notification.type === "success" ? "bg-green-900/90 border-green-500/60"  
              : notification.type === "error" ? "bg-red-900/90 border-red-500/60"  
              : "bg-gray-900/90 border-gold/60"  
            }`}  
          >  
            <div className="flex items-center gap-3">  
              <div className={`w-2 h-2 rounded-full animate-pulse ${  
                notification.type === "success" ? "bg-green-400"  
                : notification.type === "error" ? "bg-red-400"  
                : "bg-gold"  
              }`} />  
              <p className="text-white text-sm font-medium">{notification.message}</p>  
            </div>  
          </motion.div>  
        )}  
      </AnimatePresence>  
  
      <div className="max-w-6xl mx-auto py-6 sm:py-10 px-4 sm:px-6 lg:px-8 space-y-5">  
  
        {/* ─── Header ─── */}  
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>  
          <div className="flex items-start sm:items-center justify-between gap-4 flex-col sm:flex-row">  
            <div className="flex items-center gap-3">  
              <div className="w-1 h-10 bg-gradient-to-b from-gold to-yellow-600 rounded-full" />  
              <div>  
                <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight">Trade Logs</h1>  
                <p className="text-gray-400 text-sm mt-1">Every trade the bot has made — filtered, sorted, exportable</p>  
              </div>  
            </div>  
          </div>  
        </motion.div>  
  
        {/* ─── KPI Summary Strip ─── */}  
        {displaySummary && (  
          <motion.div  
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}  
            className="bg-gradient-to-br from-gray-900/60 via-black/60 to-gray-900/60 backdrop-blur-xl border border-gray-800 rounded-2xl p-5 sm:p-6"  
          >  
            <div className="grid grid-cols-3 gap-4 sm:gap-6">  
              <KPICell  
                label="Total P&L"  
                value={`${(displaySummary.total_pnl || 0) >= 0 ? "+" : "−"}$${Math.abs(displaySummary.total_pnl || 0).toFixed(2)}`}  
                valueColor={(displaySummary.total_pnl || 0) >= 0 ? "text-green-400" : "text-red-400"}  
              />  
              <KPICell  
                label="Win Rate"  
                value={`${displaySummary.win_rate || 0}%`}  
                sub={`${displaySummary.win_count || 0}W / ${displaySummary.loss_count || 0}L`}  
              />  
              <KPICell  
                label={activeTab === "live" ? "Open Positions" : "Total Trades"}  
                value={activeTab === "live" ? liveTrades.length : (displaySummary.total_trades || 0)}  
              />  
            </div>  
  
            {/* Per-asset chips */}  
            {activeTab === "closed" && (summary?.btc || summary?.gold) && (  
              <div className="mt-5 pt-5 border-t border-gray-800 flex flex-wrap gap-2">  
                {summary.btc && <AssetChip asset="BTC" stats={summary.btc} accent="orange" />}  
                {summary.gold && <AssetChip asset="GOLD" stats={summary.gold} accent="yellow" />}  
              </div>  
            )}  
          </motion.div>  
        )}  
  
        {/* ─── Tabs + Toolbar ─── */}  
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>  
          <div className="flex items-center gap-2 flex-col sm:flex-row">  
            {/* Segmented tab control */}  
            <div className="grid grid-cols-2 gap-1 p-1 bg-black/40 border border-gray-800 rounded-xl w-full sm:w-auto">  
              <button  
                onClick={() => setActiveTab("closed")}  
                className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${  
                  activeTab === "closed"  
                    ? "bg-gold/10 text-gold border border-gold/40"  
                    : "text-gray-400 hover:text-white border border-transparent"  
                }`}  
              >  
                Closed <span className="ml-1 text-[10px] text-gray-500">({closedTrades.length})</span>  
              </button>  
              <button  
                onClick={() => setActiveTab("live")}  
                className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-1.5 ${  
                  activeTab === "live"  
                    ? "bg-green-500/10 text-green-400 border border-green-500/40"  
                    : "text-gray-400 hover:text-white border border-transparent"  
                }`}  
              >  
                <span className={`w-1.5 h-1.5 rounded-full ${activeTab === "live" ? "bg-green-400 animate-pulse" : "bg-gray-600"}`} />  
                Live <span className="text-[10px] text-gray-500">({liveTrades.length})</span>  
              </button>  
            </div>  
  
            <div className="flex-1" />  
  
            {/* Toolbar */}  
            <div className="flex items-center gap-2 w-full sm:w-auto">  
              {/* Search */}  
              <div className="relative flex-1 sm:flex-none sm:w-56">  
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">  
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"/>  
                </svg>  
                <input  
                  type="text"  
                  placeholder="Search symbol…"  
                  value={searchTerm}  
                  onChange={(e) => setSearchTerm(e.target.value)}  
                  className="w-full pl-9 pr-3 py-2 bg-black/40 border border-gray-800 focus:border-gold/50 rounded-lg text-white text-sm placeholder-gray-600 outline-none transition-all"  
                />  
              </div>  
  
              <IconButton  
                onClick={() => setShowFilters(!showFilters)}  
                active={showFilters || activeFilterCount > 0}  
                badge={activeFilterCount || null}  
                title="Filters"  
              >  
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">  
                  <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd"/>  
                </svg>  
              </IconButton>  
  
              <IconButton  
                onClick={() => (activeTab === "live" ? fetchLiveTrades() : fetchTradeLogs())}  
                title="Refresh"  
              >  
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">  
                  <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd"/>  
                </svg>  
              </IconButton>  
  
              {activeTab === "closed" && filteredTrades.length > 0 && (  
                <IconButton onClick={downloadCSV} title="Export CSV">  
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">  
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd"/>  
                  </svg>  
                </IconButton>  
              )}  
            </div>  
          </div>  
  
          {/* Filter drawer */}  
          <AnimatePresence>  
            {showFilters && (  
              <motion.div  
                initial={{ opacity: 0, height: 0 }}  
                animate={{ opacity: 1, height: "auto" }}  
                exit={{ opacity: 0, height: 0 }}  
                className="overflow-hidden"  
              >  
                <div className="mt-3 bg-black/30 border border-gray-800 rounded-xl p-4 grid grid-cols-2 lg:grid-cols-4 gap-3">  
                  <SelectField label="Asset" value={filterAsset} onChange={setFilterAsset} options={[  
                    { v: "all", l: "All assets" }, { v: "BTC", l: "BTC only" }, { v: "GOLD", l: "Gold only" },  
                  ]} />  
                  <SelectField label="Side" value={filterSide} onChange={setFilterSide} options={[  
                    { v: "all", l: "All sides" }, { v: "buy", l: "Buy only" }, { v: "sell", l: "Sell only" },  
                  ]} />  
                  {activeTab === "closed" && (  
                    <SelectField label="Result" value={filterResult} onChange={setFilterResult} options={[  
                      { v: "all", l: "All results" }, { v: "profit", l: "Profits only" }, { v: "loss", l: "Losses only" },  
                    ]} />  
                  )}  
                  <SelectField label="Sort" value={sortBy} onChange={setSortBy}  
                    options={[  
                      { v: "timestamp", l: "Time" }, { v: "asset", l: "Asset" }, { v: "symbol", l: "Symbol" }, { v: "pnl", l: "P&L" },  
                    ]}  
                    trailing={  
                      <button  
                        onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}  
                        className="px-2 bg-black/40 border border-gray-800 hover:border-gold/40 rounded-lg transition-all"  
                        title={sortOrder === "asc" ? "Ascending" : "Descending"}  
                      >  
                        <svg className={`w-4 h-4 text-gray-400 transition-transform ${sortOrder === "asc" ? "rotate-180" : ""}`} fill="currentColor" viewBox="0 0 20 20">  
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"/>  
                        </svg>  
                      </button>  
                    }  
                  />  
                  {activeFilterCount > 0 && (  
                    <button  
                      onClick={clearFilters}  
                      className="col-span-2 lg:col-span-4 text-xs text-gray-400 hover:text-gold transition-colors text-left"  
                    >  
                      Clear all filters ({activeFilterCount})  
                    </button>  
                  )}  
                </div>  
              </motion.div>  
            )}  
          </AnimatePresence>  
        </motion.div>  
  
        {/* Live error banner */}  
        {activeTab === "live" && liveTradesError && (  
          <motion.div  
            initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}  
            className="bg-yellow-500/5 border border-yellow-500/40 rounded-xl p-4 flex items-start gap-3"  
          >  
            <svg className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">  
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>  
            </svg>  
            <div className="flex-1">  
              <p className="text-yellow-300 font-semibold text-sm">Live positions unavailable</p>  
              <p className="text-yellow-200/80 text-xs mt-0.5">Connect a broker to see real-time positions.</p>  
              <button onClick={() => navigate("/accounts")} className="mt-2 text-xs font-semibold text-yellow-400 hover:text-yellow-300">  
                Go to Accounts →  
              </button>  
            </div>  
          </motion.div>  
        )}  
  
        {/* ─── Trade list ─── */}  
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>  
          {filteredTrades.length === 0 ? (  
            <EmptyState  
              isFiltered={activeFilterCount > 0}  
              tab={activeTab}  
              onClear={clearFilters}  
              liveError={liveTradesError}  
              onGoAccounts={() => navigate("/accounts")}  
            />  
          ) : (  
            <div className="space-y-2">  
              <AnimatePresence initial={false}>  
                {filteredTrades.map((trade, idx) => {  
                  const id = trade.trade_id || trade.id || `${trade.symbol}-${idx}`;  
                  return (  
                    <TradeRow  
                      key={id}  
                      trade={trade}  
                      isLive={activeTab === "live"}  
                      isExpanded={expandedTrade === id}  
                      isHighlighted={highlightedId === id}  
                      onToggle={() => toggleTradeExpansion(id)}  
                      index={idx}  
                    />  
                  );  
                })}  
              </AnimatePresence>  
            </div>  
          )}  
        </motion.div>  
      </div>  
    </div>  
  );  
}  
  
/* ─────────────────────────────────────────── */  
/* Sub-components                              */  
/* ─────────────────────────────────────────── */  
  
const KPICell = ({ label, value, sub, valueColor = "text-white" }) => (  
  <div>  
    <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2">{label}</p>  
    <p className={`text-xl sm:text-2xl font-bold leading-none ${valueColor}`}>{value}</p>  
    {sub && <p className="text-[11px] text-gray-500 mt-2">{sub}</p>}  
  </div>  
);  
  
const AssetChip = ({ asset, stats, accent }) => {  
  const styles = {  
    orange: "bg-orange-500/10 border-orange-500/30 text-orange-400",  
    yellow: "bg-yellow-500/10 border-yellow-500/30 text-yellow-400",  
  };  
  const pnlColor = (stats.total_pnl || 0) >= 0 ? "text-green-400" : "text-red-400";  
  return (  
    <div className="flex items-center gap-2 px-3 py-2 bg-black/40 border border-gray-800 rounded-lg text-xs">  
      <span className={`px-2 py-0.5 rounded font-bold ${styles[accent] || ""} border`}>{asset}</span>  
      <span className="text-gray-500">{stats.total_trades} trades</span>  
      <span className="text-gray-700">·</span>  
      <span className="text-gray-400">{stats.win_rate}% win</span>  
      <span className="text-gray-700">·</span>  
      <span className={`font-bold ${pnlColor}`}>  
        {(stats.total_pnl || 0) >= 0 ? "+" : "−"}${Math.abs(stats.total_pnl || 0).toFixed(2)}  
      </span>  
      {stats.profit_factor > 0 && (  
        <>  
          <span className="text-gray-700">·</span>  
          <span className={`${stats.profit_factor >= 1.5 ? "text-green-400" : "text-yellow-400"} font-semibold`}>  
            PF {stats.profit_factor.toFixed(2)}  
          </span>  
        </>  
      )}  
    </div>  
  );  
};  
  
const IconButton = ({ children, onClick, active, badge, title }) => (  
  <button  
    onClick={onClick}  
    title={title}  
    className={`relative flex items-center justify-center w-10 h-10 rounded-lg border transition-all ${  
      active  
        ? "bg-gold/10 border-gold/40 text-gold"  
        : "bg-black/40 border-gray-800 text-gray-400 hover:border-gold/40 hover:text-gold"  
    }`}  
  >  
    {children}  
    {badge != null && (  
      <span className="absolute -top-1 -right-1 w-4 h-4 bg-gold text-black text-[10px] font-bold rounded-full flex items-center justify-center">  
        {badge}  
      </span>  
    )}  
  </button>  
);  
  
const SelectField = ({ label, value, onChange, options, trailing }) => (  
  <div>  
    <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{label}</label>  
    <div className="flex gap-1.5">  
      <select  
        value={value}  
        onChange={(e) => onChange(e.target.value)}  
        className="flex-1 px-3 py-2 bg-black/40 border border-gray-800 focus:border-gold/50 rounded-lg text-white text-sm outline-none transition-all cursor-pointer"  
      >  
        {options.map((o) => (  
          <option key={o.v} value={o.v}>{o.l}</option>  
        ))}  
      </select>  
      {trailing}  
    </div>  
  </div>  
);  
  
/* ─── Trade Row ─── */  
const TradeRow = ({ trade, isLive, isExpanded, isHighlighted, onToggle, index }) => {  
  const pnl = trade.pnl || 0;  
  const isProfitable = pnl >= 0;  
  const id = trade.trade_id || trade.id;  
  
  const assetBadge = getAssetBadge(trade.asset_type);  
  const sideStyle = trade.side?.toUpperCase() === "BUY"  
    ? "bg-blue-500/10 text-blue-400 border-blue-500/40"  
    : "bg-orange-500/10 text-orange-400 border-orange-500/40";  
  
  const roi = useMemo(() => {  
    if (!trade.entry || !trade.exit_price) return null;  
    const val = trade.side?.toUpperCase() === "BUY"  
      ? ((trade.exit_price - trade.entry) / trade.entry) * 100  
      : ((trade.entry - trade.exit_price) / trade.entry) * 100;  
    return val.toFixed(2);  
  }, [trade.entry, trade.exit_price, trade.side]);  
  
  return (  
    <motion.div  
      id={`trade-${id}`}  
      layout  
      initial={{ opacity: 0, y: 6 }}  
      animate={{  
        opacity: 1,  
        y: 0,  
        boxShadow: isHighlighted ? "0 0 0 2px rgba(212, 175, 55, 0.6)" : "0 0 0 0 rgba(0,0,0,0)",  
      }}  
      exit={{ opacity: 0, y: -6 }}  
      transition={{ delay: index * 0.015 }}  
      className={`bg-gradient-to-br from-gray-900/60 via-black/60 to-gray-900/60 backdrop-blur-xl rounded-xl border transition-all ${  
        isExpanded ? "border-gold/40" : "border-gray-800 hover:border-gold/25"  
      }`}  
    >  
      {/* Compact row */}  
      <button  
        onClick={onToggle}  
        className="w-full text-left p-3 sm:p-4 flex items-center gap-3 sm:gap-4"  
      >  
        {/* Direction indicator */}  
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center border flex-shrink-0 ${  
          isProfitable ? "bg-green-500/10 text-green-400 border-green-500/30"  
                       : "bg-red-500/10 text-red-400 border-red-500/30"  
        }`}>  
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">  
            {isProfitable ? (  
              <path fillRule="evenodd" d="M3.293 9.707a1 1 0 010-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L4.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />  
            ) : (  
              <path fillRule="evenodd" d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l4.293-4.293a1 1 0 011.414 0z" clipRule="evenodd" />  
            )}  
          </svg>  
        </div>  
  
        {/* Symbol + badges */}  
        <div className="flex-1 min-w-0">  
          <div className="flex items-center gap-1.5 flex-wrap mb-0.5">  
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${assetBadge.className}`}>  
              {assetBadge.label}  
            </span>  
            <span className="text-sm font-bold text-white truncate">{trade.symbol}</span>  
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${sideStyle}`}>  
              {trade.side?.toUpperCase()}  
            </span>  
            {isLive && (  
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-green-500/10 text-green-400 border border-green-500/40 flex items-center gap-1">  
                <span className="w-1 h-1 rounded-full bg-green-400 animate-pulse" />  
                LIVE  
              </span>  
            )}  
          </div>  
          <p className="text-[11px] text-gray-500">  
            {new Date(trade.timestamp).toLocaleString("en-US", {  
              month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",  
            })}  
          </p>  
        </div>  
  
        {/* Prices (desktop) */}  
        <div className="hidden md:flex items-center gap-4 text-xs">  
          <div className="text-right">  
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">Entry</p>  
            <p className="text-white font-semibold">${trade.entry?.toFixed(2) || "—"}</p>  
          </div>  
          {(trade.exit_price || trade.exit || (isLive && trade.current_price)) && (  
            <div className="text-right">  
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">  
                {isLive ? "Current" : "Exit"}  
              </p>  
              <p className="text-white font-semibold">  
                ${(trade.exit_price || trade.exit || trade.current_price)?.toFixed(2)}  
              </p>  
            </div>  
          )}  
        </div>  
  
        {/* P&L */}  
        <div className="text-right flex-shrink-0 min-w-[80px]">  
          <p className={`text-base sm:text-lg font-bold leading-tight ${isProfitable ? "text-green-400" : "text-red-400"}`}>  
            {isProfitable ? "+" : "−"}${Math.abs(pnl).toFixed(2)}  
          </p>  
          <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">  
            {isLive ? "Unrealized" : isProfitable ? "Profit" : "Loss"}  
          </p>  
        </div>  
  
        {/* Chevron */}  
        <motion.svg  
          animate={{ rotate: isExpanded ? 180 : 0 }}  
          transition={{ duration: 0.2 }}  
          className="w-4 h-4 text-gray-500 flex-shrink-0 hidden sm:block"  
          fill="currentColor" viewBox="0 0 20 20"  
        >  
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />  
        </motion.svg>  
      </button>  
  
      {/* Expanded details */}  
      <AnimatePresence>  
        {isExpanded && (  
          <motion.div  
            initial={{ height: 0, opacity: 0 }}  
            animate={{ height: "auto", opacity: 1 }}  
            exit={{ height: 0, opacity: 0 }}  
            transition={{ duration: 0.2 }}  
            className="overflow-hidden border-t border-gray-800"  
          >  
            <div className="p-4 sm:p-5 bg-black/30 space-y-5">  
              {/* Execution */}  
              <DetailSection title="Execution">  
                <DetailItem label="Entry" value={`$${trade.entry?.toFixed(2) || "—"}`} />  
                {(trade.exit_price || trade.exit) && <DetailItem label="Exit" value={`$${(trade.exit_price || trade.exit).toFixed(2)}`} />}  
                {isLive && trade.current_price != null && <DetailItem label="Current" value={`$${trade.current_price.toFixed(2)}`} />}  
                {(trade.size || trade.quantity || trade.trailing_size) && (  
                  <DetailItem label="Size" value={Number(trade.size || trade.quantity || trade.trailing_size).toFixed(4)} />  
                )}  
                {roi && (  
                  <DetailItem label="ROI" value={`${roi}%`} valueColor={+roi >= 0 ? "text-green-400" : "text-red-400"} />  
                )}  
                {trade.order_id && (  
                  <DetailItem label="Order ID" value={String(trade.order_id).slice(-8)} mono />  
                )}  
                {trade.exit_reason && <DetailItem label="Exit reason" value={trade.exit_reason} />}  
              </DetailSection>  
  
              {/* Risk */}  
              {(trade.atr_value || trade.trailing_rate || trade.chandelier_long || trade.chandelier_short || trade.tsl || trade.stop_loss || trade.take_profit) && (  
                <DetailSection title="Risk management">  
                  {trade.stop_loss != null && <DetailItem label="Stop loss" value={`$${Number(trade.stop_loss).toFixed(2)}`} />}  
                  {trade.take_profit != null && <DetailItem label="Take profit" value={`$${Number(trade.take_profit).toFixed(2)}`} />}  
                  {trade.tsl != null && <DetailItem label="Trailing stop" value={`$${Number(trade.tsl).toFixed(2)}`} />}  
                  {trade.atr_value != null && <DetailItem label="ATR" value={Number(trade.atr_value).toFixed(4)} />}  
                  {trade.trailing_rate != null && <DetailItem label="Trailing rate" value={`${(trade.trailing_rate * 100).toFixed(2)}%`} />}  
                  {trade.chandelier_long != null && <DetailItem label="Chandelier long" value={`$${trade.chandelier_long.toFixed(2)}`} />}  
                  {trade.chandelier_short != null && <DetailItem label="Chandelier short" value={`$${trade.chandelier_short.toFixed(2)}`} />}  
                </DetailSection>  
              )}  
  
              {/* Account */}  
              {(trade.balance || pnl) && (  
                <DetailSection title="Account impact">  
                  <DetailItem label="P&L" value={`${pnl >= 0 ? "+" : "−"}$${Math.abs(pnl).toFixed(2)}`} valueColor={pnl >= 0 ? "text-green-400" : "text-red-400"} />  
                  {trade.balance != null && <DetailItem label="Balance after" value={`$${Number(trade.balance).toFixed(2)}`} />}  
                </DetailSection>  
              )}  
            </div>  
          </motion.div>  
        )}  
      </AnimatePresence>  
    </motion.div>  
  );  
};  
  
const DetailSection = ({ title, children }) => (  
  <div>  
    <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-2">{title}</p>  
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{children}</div>  
  </div>  
);  
  
const DetailItem = ({ label, value, valueColor = "text-white", mono }) => (  
  <div>  
    <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">{label}</p>  
    <p className={`text-sm font-semibold ${valueColor} truncate ${mono ? "font-mono" : ""}`}>{value}</p>  
  </div>  
);  
  
/* ─── Empty State ─── */  
const EmptyState = ({ isFiltered, tab, onClear, liveError, onGoAccounts }) => {  
  if (isFiltered) {  
    return (  
      <div className="bg-black/30 border border-gray-800 rounded-2xl p-10 text-center">  
        <div className="w-14 h-14 mx-auto mb-4 bg-gray-800/50 border border-gray-700 rounded-full flex items-center justify-center">  
          <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">  
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />  
          </svg>  
        </div>  
        <h3 className="text-base font-semibold text-gray-200 mb-1">No trades match your filters</h3>  
        <p className="text-xs text-gray-500 max-w-sm mx-auto mb-5">  
          Try adjusting the filters, or clear them to see everything.  
        </p>  
        <button  
          onClick={onClear}  
          className="px-4 py-2 bg-gold/10 border border-gold/40 text-gold hover:bg-gold/20 text-xs font-semibold rounded-lg transition-all"  
        >  
          Clear filters  
        </button>  
      </div>  
    );  
  }  
  return (  
    <div className="bg-black/30 border border-gray-800 rounded-2xl p-12 text-center">  
      <div className="w-16 h-16 mx-auto mb-4 bg-gray-800/50 border border-gray-700 rounded-full flex items-center justify-center">  
        <svg className="w-7 h-7 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">  
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />  
        </svg>  
      </div>  
      <h3 className="text-lg font-semibold text-gray-200 mb-2">  
        {tab === "live" ? "No open positions" : "No trades yet"}  
      </h3>  
      <p className="text-xs text-gray-500 max-w-sm mx-auto">  
        {tab === "live"  
          ? liveError  
            ? "Connect a broker to see your live positions here."  
            : "You have no open positions right now."  
          : "Once the bot starts trading, every trade will show up here."}  
      </p>  
      {tab === "live" && liveError && (  
        <button  
          onClick={onGoAccounts}  
          className="mt-5 px-4 py-2 bg-gold/10 border border-gold/40 text-gold hover:bg-gold/20 text-xs font-semibold rounded-lg transition-all"  
        >  
          Go to Accounts →  
        </button>  
      )}  
    </div>  
  );  
};  
  
/* ─── Helpers ─── */  
const getAssetBadge = (assetType) => {  
  const a = assetType?.toUpperCase();  
  if (a === "BTC") return { label: "BTC", className: "bg-orange-500/10 text-orange-400 border border-orange-500/40" };  
  if (a === "GOLD") return { label: "GOLD", className: "bg-yellow-500/10 text-yellow-400 border border-yellow-500/40" };  
  return { label: assetType || "N/A", className: "bg-gray-500/10 text-gray-400 border border-gray-500/40" };  
};  
  
export default TradeLogs;  